import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { propertyFilters, propertySortOptions } from '../../data/siteContent';
import { defaultSettings } from '../konstanter/siteConfig';
import { propertyModel } from '../modeller/propertyModel';
import { siteApi } from '../tjenester/siteApi';

const propertiesPerPage = 6;

const canonicalPropertyTypes = [
  {
    title: 'Hus',
    description: 'Frittstående boliger med god plass og fleksible planløsninger for ulike behov.',
  },
  {
    title: 'Leilighet',
    description: 'Moderne boenheter med effektiv arealbruk, ofte sentral beliggenhet og enkel adkomst.',
  },
  {
    title: '2-mannsbolig',
    description: 'To selvstendige boliger på samme adresse, godt egnet for familie, utleie eller generasjonsbolig.',
  },
  {
    title: 'Tomt',
    description: 'Ubebygde tomter med muligheter for utvikling, oppføring eller langsiktig investering.',
  },
];

const normalizePropertyTypeCards = (propertyTypes) => {
  const mappedByTitle = new Map();

  (Array.isArray(propertyTypes) ? propertyTypes : []).forEach((item) => {
    const normalizedTitle = String(item?.title || '').trim().toLowerCase();

    if (!normalizedTitle) {
      return;
    }

    if (normalizedTitle === 'hus' || normalizedTitle === 'omsorgsboliger') {
      mappedByTitle.set('Hus', { ...item, title: 'Hus' });
      return;
    }

    if (normalizedTitle === 'leilighet' || normalizedTitle === 'leilighetsbygg') {
      mappedByTitle.set('Leilighet', { ...item, title: 'Leilighet' });
      return;
    }

    if (normalizedTitle === '2-mannsbolig' || normalizedTitle === '2 mannsbolig' || normalizedTitle === 'tomannsbolig') {
      mappedByTitle.set('2-mannsbolig', { ...item, title: '2-mannsbolig' });
      return;
    }

    if (normalizedTitle === 'tomt' || normalizedTitle === 'tomter') {
      mappedByTitle.set('Tomt', { ...item, title: 'Tomt' });
    }
  });

  return canonicalPropertyTypes.map((canonical) => {
    const existing = mappedByTitle.get(canonical.title);
    if (!existing) {
      return canonical;
    }

    return {
      title: canonical.title,
      description: existing.description || canonical.description,
    };
  });
};

const parseSizeNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits ? Number.parseInt(digits, 10) : 0;
};

const sortProperties = (properties, sortKey) => {
  const sorted = [...properties];

  switch (sortKey) {
    case 'nyeste':
      return sorted.sort((a, b) => (b.id || 0) - (a.id || 0));
    case 'storrelse-stor':
      return sorted.sort((a, b) => parseSizeNumber(b.size) - parseSizeNumber(a.size));
    case 'storrelse-liten':
      return sorted.sort((a, b) => parseSizeNumber(a.size) - parseSizeNumber(b.size));
    case 'navn':
      return sorted.sort((a, b) => a.name.localeCompare(b.name, 'nb'));
    default:
      return sorted.sort((a, b) => (a.id || 0) - (b.id || 0));
  }
};

export function useHomePage() {
  const [siteData, setSiteData] = useState({
    settings: defaultSettings,
    properties: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter');
  const initialStart = Number.parseInt(searchParams.get('start') ?? '0', 10);
  const initialSort = searchParams.get('sort');
  const [activeFilter, setActiveFilter] = useState(
    propertyFilters.some((filter) => filter.key === initialFilter) ? initialFilter : 'alle'
  );
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') ?? '');
  const [activeType, setActiveType] = useState(searchParams.get('type') ?? 'alle');
  const [sortKey, setSortKey] = useState(
    propertySortOptions.some((option) => option.key === initialSort) ? initialSort : 'standard'
  );
  const [currentPage, setCurrentPage] = useState(() => {
    const safeStart = Number.isNaN(initialStart) ? 0 : Math.max(0, initialStart);
    return Math.floor(safeStart / propertiesPerPage);
  });

  useEffect(() => {
    let isMounted = true;

    siteApi
      .getPublicSiteContent()
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setSiteData({
          settings: {
            ...defaultSettings,
            ...data.settings,
            propertyTypes: normalizePropertyTypeCards(data.settings?.propertyTypes),
          },
          properties: data.properties.map(propertyModel.mapWithSlug),
        });
        setLoadError('');
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        setLoadError(error.message);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const availableTypes = useMemo(() => {
    const types = siteData.properties.map((property) => property.type).filter(Boolean);
    return Array.from(new Set(types)).sort((a, b) => a.localeCompare(b, 'nb'));
  }, [siteData.properties]);

  const portfolioStats = useMemo(() => {
    const totalProperties = siteData.properties.length;
    const availableNow = siteData.properties.filter((property) => property.availability === 'ledig').length;

    return (siteData.settings.portfolioStats || [])
      .filter((stat) => !String(stat.label || '').toLowerCase().includes('sted'))
      .map((stat) => {
        const normalizedLabel = String(stat.label || '').toLowerCase();

        if (normalizedLabel.includes('eiendom')) {
          return { ...stat, value: String(totalProperties) };
        }

        if (normalizedLabel.includes('ledig')) {
          return { ...stat, value: String(availableNow).padStart(2, '0') };
        }

        return stat;
      });
  }, [siteData.properties, siteData.settings.portfolioStats]);

  const filteredProperties = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const matches = siteData.properties.filter((property) => {
      if (activeFilter !== 'alle' && property.availability !== activeFilter) {
        return false;
      }

      if (activeType !== 'alle' && property.type !== activeType) {
        return false;
      }

      if (normalizedSearch) {
        const haystack = `${property.name} ${property.address}`.toLowerCase();
        if (!haystack.includes(normalizedSearch)) {
          return false;
        }
      }

      return true;
    });

    return sortProperties(matches, sortKey);
  }, [siteData.properties, activeFilter, activeType, searchTerm, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / propertiesPerPage));
  const normalizedPage = Math.min(currentPage, totalPages - 1);
  const normalizedStartIndex = normalizedPage * propertiesPerPage;
  const visibleProperties = filteredProperties.slice(normalizedStartIndex, normalizedStartIndex + propertiesPerPage);
  const canBrowseProperties = totalPages > 1;

  useEffect(() => {
    const params = {
      filter: activeFilter,
      start: String(normalizedStartIndex),
    };

    if (searchTerm.trim()) {
      params.q = searchTerm.trim();
    }

    if (activeType !== 'alle') {
      params.type = activeType;
    }

    if (sortKey !== 'standard') {
      params.sort = sortKey;
    }

    setSearchParams(params, { replace: true });
  }, [activeFilter, activeType, normalizedStartIndex, searchTerm, sortKey, setSearchParams]);

  useEffect(() => {
    if (location.hash === '#eiendommer') {
      const propertySection = document.getElementById('eiendommer');

      if (propertySection) {
        propertySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location.hash]);

  const goToPreviousPage = () => {
    setCurrentPage((page) => Math.max(0, page - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((page) => Math.min(totalPages - 1, page + 1));
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(Math.min(Math.max(0, pageNumber), totalPages - 1));
  };

  const handleFilterChange = (filterKey) => {
    setActiveFilter(filterKey);
    setCurrentPage(0);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(0);
  };

  const handleTypeChange = (value) => {
    setActiveType(value);
    setCurrentPage(0);
  };

  const handleSortChange = (value) => {
    setSortKey(value);
    setCurrentPage(0);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setActiveType('alle');
    setSortKey('standard');
    setActiveFilter('alle');
    setCurrentPage(0);
  };

  return {
    activeFilter,
    activeType,
    availableTypes,
    canBrowseProperties,
    currentPage: normalizedPage,
    filteredProperties,
    goToNextPage,
    goToPage,
    goToPreviousPage,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
    handleTypeChange,
    isLoading,
    loadError,
    normalizedStartIndex,
    resetFilters,
    searchTerm,
    portfolioStats,
    settings: siteData.settings,
    sortKey,
    totalPages,
    visibleProperties,
  };
}